import React, { useEffect, useState, useCallback } from 'react'
import propTypes from 'prop-types'
import styled from 'styled-components'
import moment from 'moment'
import { toast } from 'react-toastify'

import { Row, Column } from '../Grid'
import { Text } from '../Text'
import { Tooltip } from '../Tooltip'
import { BookmarkPrice } from '../Icons'

import { isUserBrowserSafari } from '../../../utils/helpers'
import { isEmptyObject } from '../../../utils/formatters'
import { getBlockedSchedules } from 'services/schedule'
import { useFeatureFlags } from '../../../hooks/useFeatureFlags'
import { useUser } from 'providers/UserProvider'

const HoursCalendar = ({
  isSearch,
  schedule,
  selectSchedule,
  selected,
  handleDelete,
  consultationsData,
  daysQuantity,
  selectedEdit,
  mobileSelect,
  setSelectedEdit,
  consultationAmount,
  setSelectedDate,
  handleRemoveConsultation,
  setIsDisabledLoading,
  user,
  psychologistInfo,
  showBookmarks,
  benefitInfo
}) => {
  const [previousBlockedRange, setPreviousBlockedRange] = useState(0)
  const [weekHours, setWeekHours] = useState([])
  const [disabledHours, setDisabledHours] = useState([])
  const [closeButton, setCloseButton] = useState(false)
  const [isAllBlockedCalendar, setIsAllBlockedCalendar] = useState(false)

  const { frontend_is_outside_benefit_coverage_range_visible } = useFeatureFlags(
    'frontend_is_outside_benefit_coverage_range_visible'
  )

  const hasNoAppointment = Object.values(weekHours).every(a => a.length === 0)
  const scheduleMaxLength = isSearch ? 5 : daysQuantity

  const handleCallToastLimitExceeded = currentBlockedRange => {
    currentBlockedRange > previousBlockedRange &&
      toast.warning('O limite de uso do benefício foi atingido para o período.')
    setPreviousBlockedRange(currentBlockedRange)
  }

  const subtractDate = hour => {
    if (hour.time !== '00:00') {
      return hour.date
    }
    return moment(hour.date, 'YYYYMMDD')
      .subtract(1, 'day')
      .format('YYYYMMDD')
  }

  const addDate = hour => {
    if (hour.time !== '23:30') {
      return hour.date
    }
    return moment(hour.date, 'YYYYMMDD')
      .add(1, 'day')
      .format('YYYYMMDD')
  }

  useEffect(() => {
    if (schedule) {
      if (Object.keys(schedule)?.length > scheduleMaxLength) {
        const key = Object.keys(schedule).pop()
        delete schedule[key]
      }
    }

    const removeScheduleDuplicates = () => {
      const newSchedule = {}
      Object.keys(schedule).forEach(key => {
        newSchedule[key] = schedule[key].filter(
          (item, index, self) =>
            index === self.findIndex(t => t.date === item.date && t.time === item.time)
        )
      })

      return newSchedule
    }

    const newSchedule = removeScheduleDuplicates()

    setWeekHours(Object.values(newSchedule))
    document.querySelector('.close-btn') && setCloseButton(true)
  }, [schedule, closeButton])

  useEffect(() => {
    handleDisabledHours()
    if (!selected.length) setDisabledHours([])
  }, [])

  const handleDisabledHours = async () => {
    try {
      setIsDisabledLoading(true)
      const blockedRangeSchedules =
        selected.length && user?.contract?.features?.allow_contract_use_exceed === false
          ? await handleBlockRangeSchedules()
          : []

      handleCallToastLimitExceeded(blockedRangeSchedules.length)

      const blockedAdjacentSchedules = handleBlockAdjacentSchedules()

      const allBlockedSchedules = blockedAdjacentSchedules
        .flat()
        .concat(blockedRangeSchedules.flat())

      setDisabledHours(allBlockedSchedules)
    } catch (error) {
      console.log(error)
    } finally {
      setIsDisabledLoading(false)
    }
  }

  const handleBlockRangeSchedules = async () => {
    const flattenSchedules = Object.values(schedule).flat()

    let selectedCopy = selected
    let selectedSchedule = selectedCopy.at(-1)

    let formatedConsultations = selectedCopy.map(item =>
      moment(item?.date + item?.time, 'YYYYMMDDHH:mm')
        .format()
        .substring(0, 19)
    )

    const formatedLatest = moment(selectedSchedule?.date + selectedSchedule?.time, 'YYYYMMDDHH:mm')
      .format()
      .substring(0, 19)

    const payload = {
      professional_id: psychologistInfo?.id,
      consultations: formatedConsultations.slice(0, -1),
      latest: formatedLatest
    }

    await getBlockedSchedules(user?.id, payload)
      .then(({ data }) => {
        setIsAllBlockedCalendar(data?.content?.block_all_calendar)

        if (!data?.content?.available) {
          selected.pop()
          const indexOfSelectedSchedule = selected.indexOf(selectedSchedule)
          handleRemoveConsultation(indexOfSelectedSchedule)
          toast.error(
            'Você atingiu o número máximo de consultas do seu benefício. Para mais informações sobre limitações de consultas, entre em contato com a sua empresa.',
            {
              toastId: 'toast-id',
              autoClose: 6000
            }
          )
        }
        const blockedDates = data?.content?.block_ranges.map(item => {
          const daysBetweenDates = getDaysBetweenDates(moment(item[0]), moment(item[1]))
          return flattenSchedules.filter(schedule => daysBetweenDates.includes(schedule?.date))
        })

        return blockedDates
      })
      .catch(error => {
        console.log(error)
      })
  }

  const getDaysBetweenDates = (startDate, endDate) => {
    const now = startDate.clone()
    let dates = []

    while (now.isSameOrBefore(endDate)) {
      dates.push(now.format('YYYYMMDD'))
      now.add(1, 'days')
    }

    return dates
  }

  const handleBlockAdjacentSchedules = () => {
    const disabledHoursCopy = []

    selected.map(consultation => {
      if (!isSearch) {
        disabledHoursCopy.push({
          id: consultation.id,
          time: moment(consultation.time, 'HH:mm')
            .add(30, 'minutes')
            .format('HH:mm'),
          date: addDate(consultation)
        })
        disabledHoursCopy.push({
          id: consultation.id,
          time: moment(consultation.time, 'HH:mm')
            .subtract(30, 'minutes')
            .format('HH:mm'),
          date: subtractDate(consultation)
        })
      }
    })

    return disabledHoursCopy
  }

  const handleSelectedSchedule = useCallback(
    hour => {
      const alreadyExists = selected?.filter(x => x.id.includes(hour.id))

      if (alreadyExists.length || handleContractEndDate(hour)) return

      if (alreadyExists.length) {
        handleDelete(hour.id)
        return
      }

      if (
        !document.querySelector('.close-btn') &&
        !closeButton &&
        selected.length === consultationAmount &&
        !isSearch
      )
        return

      if ((document.querySelector('.close-btn') || closeButton) && !isSearch)
        handleDelete(selectedEdit)

      if (mobileSelect) setSelectedEdit(hour.id)

      if (
        !alreadyExists.length > 0 &&
        hour.status === 'available' &&
        !disabledHours.some(item => item.time === hour.time && item.date === hour.date)
      ) {
        selected.filter(op => !alreadyExists[0]?.includes(op))
        selectSchedule(hour)
      }
      setCloseButton(false)
      setSelectedDate(hour.date + hour.time)
      if (!isEmptyObject(hour)) selected.push(hour)
    },

    [selected, selectSchedule]
  )

  const handleContractEndDate = hours => {
    if (
      user?.contract?.valid_until &&
      !user?.contract?.features?.allow_schedulling_after_contract_range
    ) {
      const contractValidityLimit = moment(user?.contract?.valid_until).format('YYYYMMDD')
      const isExceededLimit = moment(hours?.date).isAfter(contractValidityLimit)

      return isExceededLimit
    }
  }

  const handleUnvailableHour = hour => {
    const alreadyExists = selected?.filter(x => x.id.includes(hour.id))

    return (
      alreadyExists.length ||
      handleContractEndDate(hour) ||
      hour.status === 'unavailable' ||
      disabledHours.some(item => item.time === hour.time && item.date === hour.date)
    )
  }

  return (
    <>
      {!hasNoAppointment ? (
        <HoursContainer id='hours-calendar' justifyContent='flex-start'>
          {weekHours.map((daysHours, indexColumnsDays) => (
            <DayColumn
              isSearch={isSearch}
              key={JSON.stringify({ app: daysHours, index: indexColumnsDays })}
            >
              {daysHours.map((hour, indexRowsHours) => (
                <HoursRow
                  id={hour.id}
                  key={hour.id}
                  isSearch={isSearch}
                  isSafari={isUserBrowserSafari()}
                >
                  {consultationsData?.find(
                    selectedHour => selectedHour[2] === hour.id && selectedHour[0] !== 'schedulable'
                  ) ? (
                    <CustomTooltip
                      indexRowsHours={indexRowsHours}
                      indexColumnsDays={indexColumnsDays}
                      variant={indexRowsHours === 0 ? 'down' : ''}
                      hoverElement={
                        <Numbers
                          onClick={() =>
                            !handleUnvailableHour(hour) && handleSelectedSchedule(hour)
                          }
                          variant='small'
                          isSearch={isSearch}
                          isScheduled={selected.find(selectedHour => selectedHour.id === hour.id)}
                          py='5px'
                          fontWeight='bold'
                          disabled={
                            handleContractEndDate(hour) ||
                            hour.status === 'unavailable' ||
                            disabledHours.some(
                              item => item.time === hour.time && item.date === hour.date
                            )
                          }
                          checked={selected.filter(x => x.id === hour.id)}
                        >
                          {moment(hour.time, 'HH:mm').format('HH:mm')}
                        </Numbers>
                      }
                      backgroundColor='#f8f8f8'
                      boxShadow='0px 1px 8px #00000024'
                      borderRadius='4px'
                      width='70px'
                      py='5px'
                      px='5px'
                    >
                      <Text variant='tiny' color='#4e5657' fontWeight='bold'>
                        Consulta{' '}
                        {consultationsData?.findIndex(selectedHour => selectedHour[2] === hour.id) +
                          1}
                      </Text>
                    </CustomTooltip>
                  ) : (
                    <Column position='relative'>
                      {frontend_is_outside_benefit_coverage_range_visible &&
                        showBookmarks &&
                        selected.length >= benefitInfo.total - benefitInfo.used &&
                        hour?.contract_price?.value > 0 &&
                        !handleUnvailableHour(hour) && (
                          <Row position='absolute' top='13px' left='37px'>
                            <BookmarkPrice size='16px' color='#FFA938' />
                          </Row>
                        )}
                      <Numbers
                        onClick={() => !handleUnvailableHour(hour) && handleSelectedSchedule(hour)}
                        variant='small'
                        isSearch={isSearch}
                        isScheduled={selected.find(selectedHour => selectedHour.id === hour.id)}
                        py='5px'
                        fontWeight='bold'
                        disabled={
                          handleContractEndDate(hour) ||
                          isAllBlockedCalendar ||
                          hour.status === 'unavailable' ||
                          disabledHours.some(
                            item => item.time === hour.time && item.date === hour.date
                          )
                        }
                        checked={selected.filter(x => x.id === hour.id)}
                      >
                        {moment(hour.time, 'HH:mm').format('HH:mm')}
                      </Numbers>
                    </Column>
                  )}
                  {!isSearch &&
                    selected.find(selectedHour => selectedHour.id === hour.id) &&
                    consultationsData?.find(
                      selectedHour =>
                        selectedHour[2] === hour.id && selectedHour[0] === 'schedulable'
                    ) && <Column m='10px 0 0 -10px' className='close-btn' />}
                </HoursRow>
              ))}
            </DayColumn>
          ))}
        </HoursContainer>
      ) : (
        <Column alignItems='center' width={['240px', '310px']} mt='110px'>
          <Text variant='tiny'>Não há horários disponíveis para essas datas!</Text>
        </Column>
      )}
    </>
  )
}

HoursCalendar.propTypes = {
  schedule: propTypes.object.isRequired,
  selectSchedule: propTypes.func.isRequired
}

const DayColumn = styled(Column)(
  ({ isSearch }) => `

  width: ${isSearch ? '64px' : '72px'};

  @media (max-width: 320px) {
    width: ${isSearch ? '47px' : '70px'};
  }
  @media (min-width: 321px) {
    width: ${isSearch ? '55px' : '70px'};
  }
  @media (min-width: 425px) {
    width: ${isSearch ? '58px' : '70px'};
  }
`
)

const HoursContainer = styled(Row)(
  ({ theme: { colors } }) => `
  overflow: auto;
  max-height: 250px;
  scrollbar-width: thin;
  min-height: 100px;
  width: fit-content;

  ::-webkit-scrollbar {
    width: 3px;
    height: 3px;
    background-color: ${colors.gray.n200};
  }
  ::-webkit-scrollbar-thumb {
    background-color: #babac0;
    border-radius: 16px;
  }
  ::-webkit-scrollbar-button {
    display: none;
  }
`
)

const HoursRow = styled(Row)(
  ({ isSearch, isSafari }) =>
    `
  ${
    isSearch
      ? `
      max-width: 43px;

      @media (max-width: 320px) {
        margin-right: 4px;
      }
      @media (min-width: 321px) {
        margin-right: 12px;
      }
      @media (min-width: 425px) {
        margin-right: 0px;

      }
    `
      : `margin-right: 20px;
         max-width: 50px;
      `
  }
  ${isSafari &&
    `
      display: -webkit-box;
     `}

`
)

const CustomTooltip = styled(Tooltip)(
  ({ indexRowsHours, indexColumnsDays }) => `
  margin-top: -20px;
  margin-left: -10px;
  ${indexRowsHours === 0 &&
    `
    margin-top: 32px;
    margin-left: 55px;
    `};
  ${indexColumnsDays === 0 &&
    indexRowsHours === 0 &&
    `
    margin-top: 32px;
    left: 0 !important;
    margin-left: 0;
    `};
  ${indexColumnsDays === 0 &&
    indexRowsHours !== 0 &&
    `
    margin-left: 0px;
    `};
  ${indexColumnsDays === 6 &&
    `
    margin-left: 43px;
    `};
  `
)

const Numbers = styled(Text)(
  ({ theme: { colors }, disabled, checked, isScheduled, isSearch }) =>
    `
  color: ${disabled ? colors.gray.n300 : '#27AE60'};
  cursor: pointer;
  font-weight: 600;
  font-size: 12px;
  border-radius: 3px;
  background-color: ${disabled ? 'transparent' : '#27AE601C'};


  ${
    isSearch
      ? `min-width: 43px;
         margin-top: 12px;`
      : `min-width: 50px;
         margin-top: 15px;`
  }

  ${
    disabled
      ? 'cursor: not-allowed;'
      : `&:hover {
        background-color: #27AE60;
        color: ${colors.white};
        font-weight: bold;
      }`
  }
  ${checked.length > 0 &&
    `
      border-radius: 3px;
      color: #DB0000;
      background-color: #FBE3E3;
      cursor: not-allowed;
      &:hover {
        color: #DB0000;
        background-color: #FBE3E3;
      }
    `}
  ${isScheduled &&
    ` color: ${colors.white};
      background-color: ${colors.primary};`}

  ${isSearch &&
    checked.length > 0 &&
    ` color: ${colors.white};
      background-color: ${colors.primary};
      cursor: pointer;
      &:hover {
        color: ${colors.white};
        background-color: ${colors.primary};
      }
  `}
`
)

export { HoursCalendar }
